import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {ItemModifyingState, ItemModifyingAction, SelectedTimelineItem, TimelineItemType} from './timeline.types';

export const TIMELINE_MIN_WIDTH_PER_SECOND = 5;
export const TIMELINE_MAX_WIDTH_PER_SECOND = 500;

interface SubtitlePushedPosition {
  startTime: number;
  endTime: number;
}

interface TimelineState {
  widthPerSecond: number;
  timelineHeight: number;
  isPanning: boolean;
  /** ID of the currently hovered timeline item */
  hoveredItemId?: string;
  /** Current item being modified (dragged or resized). Undefined when no modification in progress. */
  itemModifying?: ItemModifyingState;
  timelineHoverPosition?: number;
  minWidthAccordingToPosterDuration: number;
  /** Currently selected timeline item (page, subtitle, audio, etc.). Only one can be selected at a time. */
  selectedTimelineItem?: SelectedTimelineItem;
  /** Temporary positions for subtitles being pushed during a resize operation */
  pushedSubtitlePositions: Record<string, SubtitlePushedPosition>;
  /** Whether the seekbar is currently being dragged */
  isSeekbarDragging: boolean;
}

const initialState: TimelineState = {
  widthPerSecond: 47,
  timelineHeight: 0,
  isPanning: false,
  hoveredItemId: undefined,
  itemModifying: undefined,
  timelineHoverPosition: undefined,
  minWidthAccordingToPosterDuration: TIMELINE_MIN_WIDTH_PER_SECOND,
  selectedTimelineItem: undefined,
  pushedSubtitlePositions: {},
  isSeekbarDragging: false,
};

/** Payload for starting an item modification (drag or resize) */
interface StartItemModifyingPayload {
  itemId: string;
  action: ItemModifyingAction;
  startTime: number;
  endTime: number;
  timelineItemType: TimelineItemType;
}

/** Payload for updating an item's position/times during modification */
interface UpdateItemModifyingPayload {
  startTime: number;
  endTime: number;
}

export const timelineSlice = createSlice({
  name: 'timeline',
  initialState,
  reducers: {
    updateTimelineWidthPerSecond: (state, action: PayloadAction<number>): void => {
      const minWidth = Math.max(TIMELINE_MIN_WIDTH_PER_SECOND, state.minWidthAccordingToPosterDuration);
      if (action.payload < minWidth) {
        state.widthPerSecond = minWidth;
      } else if (action.payload > TIMELINE_MAX_WIDTH_PER_SECOND) {
        state.widthPerSecond = TIMELINE_MAX_WIDTH_PER_SECOND;
      } else {
        state.widthPerSecond = action.payload;
      }
    },
    updatePanningStatus: (state, action: PayloadAction<boolean>): void => {
      state.isPanning = action.payload;
    },
    setHoveredItemId: (state, action: PayloadAction<string | undefined>): void => {
      state.hoveredItemId = action.payload;
    },
    /**
     * Start modifying an item (drag or resize).
     * Sets up the itemModifying state with initial values.
     */
    startItemModifying: (state, action: PayloadAction<StartItemModifyingPayload>): void => {
      state.itemModifying = {
        itemId: action.payload.itemId,
        action: action.payload.action,
        startTime: action.payload.startTime,
        endTime: action.payload.endTime,
        timelineItemType: action.payload.timelineItemType,
      };
    },
    /**
     * Update the current modification in progress.
     * Called during pointermove to update visual position.
     */
    updateItemModifying: (state, action: PayloadAction<UpdateItemModifyingPayload>): void => {
      if (state.itemModifying) {
        state.itemModifying.startTime = action.payload.startTime;
        state.itemModifying.endTime = action.payload.endTime;
      }
    },
    /**
     * End the current modification.
     * Clears the itemModifying state and any pushed subtitle positions.
     */
    endItemModifying: (state): void => {
      state.itemModifying = undefined;
      state.pushedSubtitlePositions = {};
    },
    /**
     * Set temporary pushed positions for subtitles being displaced during a resize.
     * Replaces any previously set pushed positions.
     */
    setPushedSubtitlePositions: (state, action: PayloadAction<Record<string, SubtitlePushedPosition>>): void => {
      state.pushedSubtitlePositions = action.payload;
    },
    updateTimelineHoverPosition: (state, action: PayloadAction<number | undefined>): void => {
      state.timelineHoverPosition = action.payload;
    },
    updatePosterMinWidthAccordingToDuration: (state, action: PayloadAction<number>): void => {
      state.minWidthAccordingToPosterDuration = action.payload;
    },
    updateTimelineHeight: (state, action: PayloadAction<number>): void => {
      state.timelineHeight = action.payload;
    },
    setSeekbarDragging: (state, action: PayloadAction<boolean>): void => {
      state.isSeekbarDragging = action.payload;
    },
    /**
     * Set the selected timeline item.
     * Only one item can be selected at a time - setting a new item automatically clears the previous selection.
     */
    setSelectedTimelineItem: (state, action: PayloadAction<SelectedTimelineItem>): void => {
      state.selectedTimelineItem = action.payload;
    },
    /**
     * Clear the selected timeline item.
     */
    clearSelectedTimelineItem: (state): void => {
      state.selectedTimelineItem = undefined;
    },
  },
});

export const {
  updateTimelineWidthPerSecond,
  updateTimelineHeight,
  updatePanningStatus,
  setHoveredItemId,
  startItemModifying,
  updateItemModifying,
  endItemModifying,
  updateTimelineHoverPosition,
  updatePosterMinWidthAccordingToDuration,
  setSelectedTimelineItem,
  clearSelectedTimelineItem,
  setPushedSubtitlePositions,
  setSeekbarDragging,
} = timelineSlice.actions;

export const timelineReducer = timelineSlice.reducer;
